import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { injectIntl } from 'react-intl';
import { rgba } from 'polished';
import styled from 'styled-components';
import { withTour } from '@reactour/tour';
import { Button as AntdButton, Modal } from 'antd';

import { getCompanyTabsSelector } from 'selectors/company';
import { getUserByIdSelector, getCurrentUserIdSelector } from 'selectors/user';

import Button from 'components/Shared/Common/Button';
import BrandLogo from 'components/Shared/Company/BrandLogo/container';

import Firebase, { ON_BOARDING_MAX_VIEWS } from 'utils/firebase';

import { colors } from 'common/themes/Colors';

import placeholder from 'statics/images/placeholder/global-placeholder.png';
import placeholderRetina from 'statics/images/placeholder/global-placeholder@2x.png';
import placeholder4k from 'statics/images/placeholder/global-placeholder@3x.png';

const StyledWelcome = styled.div`
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 222;
    transition: all 250ms ease;
    background-color: ${props => props.theme.mainColor1};
    >img{
        object-fit: cover;
        max-width: 100%;
        display: block;
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translateX(-50%) translateY(-50%);
    }
    .overlay{
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background-color: ${props => rgba(props.theme.mainColor1,.97)};
    }
    .brand-logo{
        max-width: 100px;
        display: flex;
        align-items: flex-start;
        align-self: flex-start;
        margin-bottom: 20px;
        img{
            display: flex;
            align-items: flex-start;
            align-self: flex-start;
            max-width: 100%;
        }
    }
    .global-placeholder{
        &-content{
            max-width: 420px;
            display: flex;
            flex-direction: column;
            position: relative;
            z-index: 3;
            .text{
                font-size: 18px;
                color: ${colors.white};
                font-weight: 500;
                margin-bottom: 20px;
            }
            .action{
                display: flex;
                justify-content: flex-end;
                margin-top: 20px;
                .btn{
                    margin-right: 20px;
                    &:last-child{
                        margin-right: 0;
                    }
                }
            }
        }
    }
`;

const StyledModal = styled(Modal)`
    width: auto !important;

    .ant-modal-content {
        .ant-modal-body {
            width: 70vw !important;
        }
    }
`;

const formatMessages = intl => ({ firstname }) => ({
    welcome: intl.formatMessage({ id: 'tour.intro.welcome' }, { 
        b: chunks => <b>{chunks}</b>,
        firstname,
    }),
    welcomeText: intl.formatMessage({ id: 'tour.intro.text' }),
    welcomeTextUpgrade: intl.formatMessage({ id: 'tour.intro.text.upgrade' }),
    welcomeSubText: intl.formatMessage({ id: 'tour.intro.subtext' }),
    cancel: intl.formatMessage({ id: 'tour.intro.button.cancel' }),
    start: intl.formatMessage({ id: 'tour.intro.button.start' }),
    continue: intl.formatMessage({ id: 'rtc.modal.unsupportedScreenSharing.continue' }),
    modalTitle: intl.formatMessage({ id: 'tour.intro.modal.title' }),
    buttonCancel: intl.formatMessage({ id: 'global.button.ignore' }),
    buttonValid: intl.formatMessage({ id: 'global.button.markAsSeenDescription' }),
});

const CAMPAIGNS_MODAL_TOUR = 'CAMPAIGNS_MODAL_TOUR';

const WelcomeModal = ({
    _user,
    isNewlyCreatedUser,
    i18n,
    intl,
}) => {
    const [enableWelcomeModal, setEnableWelcomeModal] = useState(false);
    const [activeCampaigns, setActiveCampaigns] = useState([]);
    const [currentVideoIndex, setCurrentVideoIndex] = useState(0);
    const videoRef = useRef(null);
    // eslint-disable-next-line
    const [_, setSettings] = useState(null);

    const fetchActiveCampaignsByUser = useCallback(async () => {
        const campaigns = await Firebase.findActiveCampaigns();
        if(!campaigns) {
            return;
        }

        const newCampaigns = (campaigns||[]).filter(campaign => {
            const isCampaignAlreadyViewed = localStorage.getItem(`${CAMPAIGNS_MODAL_TOUR}-${campaign.id}`);
            return isCampaignAlreadyViewed !== 'true';
        });

        if (newCampaigns.length) {
            const userSettings = await Firebase.getUserSettings(_user);
            const actCampaigns = (newCampaigns||[]).filter(campaign => {
                const { viewsCount, isViewed } = userSettings?.onBoarding?.campaigns?.[campaign.id] || {};
                const isViewedOrMaxViews = viewsCount >= ON_BOARDING_MAX_VIEWS || isViewed;

                if (isViewedOrMaxViews) {
                    localStorage.setItem(`${CAMPAIGNS_MODAL_TOUR}-${campaign.id}`, 'true');
                    return false;
                }
                return true;
            });

            if (actCampaigns.length) {
                setSettings(userSettings);
                setEnableWelcomeModal(true);
                setActiveCampaigns(actCampaigns);
            }
        }
    }, [_user]);

    const handleIncrementOnBoarding = useCallback((campaign, markedAsSeen) => {
        setSettings(prevSettings => {
            const newBaseSettings = {
                ...prevSettings,
                onBoarding: {
                    ...prevSettings?.onBoarding,
                    campaigns: {
                        ...prevSettings?.onBoarding?.campaigns,
                        [campaign.id]: {
                            id: campaign.id,
                            isViewed: !!markedAsSeen,
                            viewsCount: 1,
                        }
                    }
                },
            }

            if (prevSettings?.onBoarding?.campaigns?.[campaign.id]) {
                Firebase.updateOnBoarding(campaign.id, prevSettings, markedAsSeen);
                newBaseSettings.onBoarding.campaigns[campaign.id] = {
                    ...prevSettings.onBoarding.campaigns[campaign.id],
                    isViewed: !!markedAsSeen,
                    viewsCount: prevSettings.onBoarding.campaigns[campaign.id].viewsCount + 1,
                }

                return newBaseSettings;
            }

            Firebase.createOnBoarding(campaign.id, prevSettings, !!markedAsSeen);
            return newBaseSettings;
        });
    }, []);

    const handleClose = useCallback(() => {
        if (videoRef.current) {
            videoRef.current.pause();
            videoRef.current.currentTime = 0;
        }
        handleIncrementOnBoarding(activeCampaigns[currentVideoIndex]);
        setEnableWelcomeModal(false);
    }, [handleIncrementOnBoarding, currentVideoIndex, activeCampaigns]);

    const handleVideoEnd = useCallback(() => {
        if (videoRef.current) {
            videoRef.current.pause();
            videoRef.current.currentTime = 0;
        }
        handleIncrementOnBoarding(activeCampaigns[currentVideoIndex], true);
        if (currentVideoIndex < activeCampaigns.length - 1) {
            setCurrentVideoIndex(currentVideoIndex + 1);
        } else {
            setEnableWelcomeModal(false);
        }
    }, [currentVideoIndex, activeCampaigns, handleIncrementOnBoarding]);

    const language = useMemo(() => intl.locale.split('-')[0], [intl.locale]);
    const videoSrc = useMemo(() => {
        const src = activeCampaigns[currentVideoIndex]?.[`dashboardVideoSrc-${language}`];
        if (src) return src;

        return activeCampaigns[currentVideoIndex]?.dashboardVideoSrc;
    }, [language, activeCampaigns, currentVideoIndex]);

    useEffect(() => {
        if (!isNewlyCreatedUser) {
            fetchActiveCampaignsByUser();
        }
    }, []);

    return (
        <StyledModal
            open={enableWelcomeModal}
            title={i18n.modalTitle}
            footer={(
                <>
                    <AntdButton onClick={handleClose}>
                        {i18n.buttonCancel}
                    </AntdButton>
                    <AntdButton
                        type="primary"
                        color="green"
                        onClick={handleVideoEnd}
                    >
                        {currentVideoIndex < activeCampaigns.length - 1 ? i18n.continue : i18n.buttonValid}
                    </AntdButton>
                </>
            )}
            onCancel={handleClose}
            centered
        >
            <video
                ref={videoRef}
                title={`onboarding-video-${activeCampaigns[currentVideoIndex]?.id}`}
                src={videoSrc}
                width="100%"
                height="100%"
                onEnded={handleVideoEnd}
                controls
            />
        </StyledModal>
    );
};

const WelcomeTour = ({
    isWelcomeOpened,
    enableWelcome,
    handleCancel,
    handleSubmit,
    i18n,
    isUpgrade
}) => {
    if (!enableWelcome || !isWelcomeOpened) return null;

    return (
        <StyledWelcome>
            <img
                alt="Welcome"
                src={placeholder}
                srcSet={`${placeholderRetina} 2x,
                    ${placeholder4k} 3x`}
            />
            <span className="overlay">&nbsp;</span>
            <div className="global-placeholder-content">
                <div className="brand-logo">
                    <BrandLogo size="md" type="shortLogo" />
                </div>
                <div className="text">
                    {i18n.welcome},<br /><br />
                    {isUpgrade ? i18n.welcomeTextUpgrade : i18n.welcomeText}
                </div>
                <div className="text">
                    {i18n.welcomeSubText}
                </div>
                <div className="action">
                    <Button
                        onClick={handleCancel}
                        color="secondary"
                        size="md"
                    >
                        {i18n.cancel}
                    </Button>
                    <Button
                        onClick={handleSubmit}
                        color="primary"
                        animation="pulse"
                        size="md"
                    >
                        {i18n.start}
                    </Button>
                </div>
            </div>
        </StyledWelcome>
    );
};

const Welcome = ({
    intl,
    firstname,
    onSubmit,
    onCancel,
    _user,
    isNewlyCreatedUser,
    isUpgrade,
    enableWelcome,
    setIsOpen
}) => {
    const [isWelcomeOpened, setIsWelcomeOpened] = useState(true);
    const i18n = formatMessages(intl)({ firstname });

    const handleSubmit = useCallback(() => {
        setIsOpen(true);
        onSubmit();
        setIsWelcomeOpened(false);
    }, [setIsOpen]);

    const handleCancel = useCallback(() => {
        onCancel();
        setIsWelcomeOpened(false);
    }, [setIsOpen]);

    return (
        <>
            <WelcomeModal
                _user={_user}
                isNewlyCreatedUser={isNewlyCreatedUser}
                i18n={i18n}
                intl={intl}
            />
            <WelcomeTour
                isWelcomeOpened={isWelcomeOpened}
                enableWelcome={enableWelcome}
                handleCancel={handleCancel}
                handleSubmit={handleSubmit}
                i18n={i18n}
                isUpgrade={isUpgrade}
            />
        </>
    )
}

Welcome.propTypes = {
    intl: PropTypes.any.isRequired,
    firstname: PropTypes.string,
    onSubmit: PropTypes.func.isRequired,
    onCancel: PropTypes.func.isRequired,
};

const ONE_DAY = 1000 * 3600 * 24;
const NEW_USER_DURATION = ONE_DAY * 90;
const localStorageKey = 'skipCallTour';

const mapStateToProps = state => {
    const _user = getCurrentUserIdSelector(state);
    const { firstname, createdAt } = getUserByIdSelector(state, _user) || {};
    const now = Math.round(Date.now());
    const isNewlyCreatedUser = !!(createdAt && now - Date.parse(createdAt) < NEW_USER_DURATION);
    const isAldreadyOpened = !(!localStorage.getItem(localStorageKey) || localStorage.getItem(localStorageKey) === 'false');
    const companyTabs = getCompanyTabsSelector(state);
    const isUpgrade = (companyTabs || []).some(x => x.name === 'neoconnect');

    return ({
        firstname,
        enableWelcome: isNewlyCreatedUser && !isAldreadyOpened,
        _user,
        isNewlyCreatedUser,
        isUpgrade
    });
};

const mapDispatchToProps = () => ({
    onSubmit: () => {
        localStorage.setItem(localStorageKey, true);
    },
    onCancel: () => {
        localStorage.setItem(localStorageKey, true);
    },
});

export default connect(mapStateToProps, mapDispatchToProps)(injectIntl(withTour(Welcome)));
